/** @jsxImportSource @emotion/react */
import {DISABLED, MediumText, PAGE_COLUMN, PAGE_CONTENT, PAGE_TITLE} from '../styles/styles';
import * as React from 'react';
import * as R from 'ramda';
import {Column} from '../layout/flexbox';
import {ProductMappingTable} from './ProductMappingTable';
import {IAppState} from '../core/redux/IAppState';
import {shallowEqual, useDispatch, useSelector, useStore} from 'react-redux';
import { RTPMappingProduct, SiriuswareMappingProduct} from './productMappings';
import {css} from '@emotion/react';
import {UpdateResortMappingsAction} from './updateResortMappingAction';
import {useCallback, useEffect} from 'react';
import IosSpinner from '../forms/elements/loader/IosSpinner';
import { UpdateResortMappingsRTPAction } from './updateMappingErrorsRTPAction';
import { UpdateResortMappingsSWAction } from './updateMappingErrorsSWAction';
import { ROOT_PATH } from '../core/routes';
import { getLoading } from '../core/redux/api/module';
import { ACTION_TYPE } from '../core/redux/actionType';
import { clearApiErrorAction } from '../core/redux/api/clearApiErrorAction';
import { showSnackbarAction } from '../snackbar/snackbarAction';
import { ApiError } from '../core/redux/api/apiError';
import sync from '../assets/icons/refresh-icon.svg';
import pencil from '../assets/icons/pencil.svg';
import save from '../assets/icons/disk.svg';
import cancel from '../assets/icons/xCircle.svg';
import { getResortMappingErrorAction, isResortMappingErrorLoading, isResortMappingErrorSuppressed, resortMappingErrorRequestedAction, resortMappingErrorsOutOfDateAction, shouldLoadResortMappingErrors } from './getResortMappingErrorAction';
import infoImg from '../assets/icons/info.svg';
import download from '../assets/icons/download.svg';
import upload from '../assets/icons/upload.svg';
import { TooltipFC } from '../layout/TooltipFC';
import { ProductMappingUploadModal } from './ProductMappingUploadModal';
import { FADE_IN_KEYFRAMES } from '../styles/animations';
import { BLUE_CREEK, DARK_BLUE_CREEK, LIGHT_GREY, WHITE } from '../styles/colors';
import { getResource } from '../language/resources/getResource';
import { getProductHydrationErrorsAction, isProductHydrationErrorsSuppressed } from './getProductHydrationErrorsAction';
import { isResortMappingLoading } from './getResortMappingAction';
import { ResortMappingSnapshot, revertResortMappingRowsAction } from './revertResortMappingRowsAction';
import { getProductMappingLoadStatus, getSyncHeldUntil, hasFailed, isBusy, isConfigForSelectedResort, LoadStatus, ProductMappingLoadStatus, showsHydrationErrors, shownSyncTime } from './productMappingLoadStatus';
import { useTimeAhead } from '../language/hooks/useTimeAhead';
import { formatTime } from '../language/time';
import { setProductMappingUnsavedEditsAction } from './setProductMappingUnsavedEditsAction';
import { StyledBoxedMessage } from '../forms/elements/boxedMessage/StyledBoxedMessage';
import { BoxedErrorInverseNoBorder, BoxedNoDataMessage } from '../forms/elements/boxedMessage/boxedMessageStyles';
import ErrorView from '../forms/ErrorView';
import { cloneDeep } from 'lodash';
import { Navigate } from 'react-router-dom';

const UPDATE_ACTIONS = [
  ACTION_TYPE.UPDATE_RESORT_MAPPING_REQUEST,
] as string[];

// The per-row re-check after a save
const RECHECK_ACTIONS = [
  ACTION_TYPE.UPDATE_PRODUCT_MAPPING_REQUEST,
  ACTION_TYPE.UPDATE_PRODUCT_MAPPING_REQUEST_SW,
] as string[];

type SyncCheck = keyof Pick<ProductMappingLoadStatus, 'mappingStatus' | 'hydrationErrors'>;

const styles = {
  titleRow: css`
    display: flex;
    flex-direction: row;
    width: 100%;
    margin-bottom: 10px;
  `,
  titleRowLeft: css`
    display: flex;
    flex-direction: row;
    width: 50%;
    justify-content: flex-start;
    gap: 10px;
  `,
  titleRowRight: css`
    margin-left: 50px;
    display: flex;
    flex-direction: row;
    width: 50%;
    justify-content: flex-end;
    gap: 10px;
  `,
  editButton: css`
    position: sticky;
    position: -webkit-sticky;
    top: 70px;
  `,
  mappingButtonImage: css`
    width: 15px;
    margin-right: 7px;
  `,
  mappingButtonFlex: css`
    display: flex;
    alignItems: center;
  `,
}

const mappingButton = css`
  text-align: center;
  text-decoration: none;
  text-wrap: nowrap;
  ${MediumText};
  animation: 1s ${FADE_IN_KEYFRAMES};
  cursor: pointer;

  background-color: ${WHITE}; 
  border: solid 1px ${BLUE_CREEK};
  padding: 14px 31px;
  color: ${BLUE_CREEK};
  
  &:hover {
    background-color: ${LIGHT_GREY}; 
    color: ${DARK_BLUE_CREEK};
  }

  &:disabled {
    ${DISABLED};
  }
`;

const columnStyles = css `
  ${PAGE_COLUMN};
  width: fit-content;
`;

const infoStyle = css`
  width: 20px;
  height: 20px;
  margin-bottom: -11px; /* align image with text */
  margin-left: 5px;
  pointer-events: initial;
`;

const ioSpinner = css`
  z-index: 100; 
  position: fixed; 
  top: 45vh; 
  left: 50vw;

  /* Hidden for its first 250ms, so a quick wait never flashes it. CSS because the edit-mode render blocks timers */
  animation-delay: 250ms;
  animation-fill-mode: backwards;
`;

const ProductMappingPage = () => {

  const dispatch = useDispatch();
  const {resortConfig, loggedInResort, isLoading, saveErrors, isSaving,
    recheckErrors, isLoadingMappingErrors, resortMappingHistories, userRole, resources, timeFormat,
    configIsForSelectedResort} = useSelector((state: IAppState) => ({
    resortConfig: state.resortToolsConfig,
    loggedInResort: state.selectedResort,
    isLoading: isResortMappingLoading(state, state.selectedResort.resortAbbreviation),
    saveErrors: state.error.filter((key, action) => UPDATE_ACTIONS.includes(action.actionType)).getValues(),
    isSaving: !!getLoading(
      state, 
      ACTION_TYPE.UPDATE_RESORT_MAPPING_REQUEST,
      ACTION_TYPE.UPDATE_RESORT_MAPPING_REQUEST,
    ),
    recheckErrors: state.error.filter((key, action) => RECHECK_ACTIONS.includes(action.actionType)).getValues(),
    isLoadingMappingErrors: isResortMappingErrorLoading(state, state.selectedResort.resortAbbreviation)
      || !!getLoading(state, ACTION_TYPE.UPDATE_PRODUCT_MAPPING_REQUEST, ACTION_TYPE.UPDATE_PRODUCT_MAPPING_REQUEST )
      || !!getLoading(state, ACTION_TYPE.UPDATE_PRODUCT_MAPPING_REQUEST_SW, ACTION_TYPE.UPDATE_PRODUCT_MAPPING_REQUEST_SW ),
    resortMappingHistories: state.resortMappingHistories,
    resources: state.locale,
    timeFormat: state.defaultTimeFormat,
    userRole: state.userState ? state.userState.role : 'agent',
    configIsForSelectedResort: isConfigForSelectedResort(state),
  }));
  const status = useSelector(getProductMappingLoadStatus, shallowEqual);
  // Too many failures hold Sync. The hold ends in wall-clock time, not app state, so the page re-renders itself then.
  const syncHeldUntil = useTimeAhead(useSelector(getSyncHeldUntil));
  const syncHeldMessage = syncHeldUntil === undefined
    ? undefined
    : getResource(resources, 'PRODUCT_MAPPING_PAGE_SYNC_HELD', formatTime(shownSyncTime(syncHeldUntil), timeFormat));
  // The breaker's cool-down elapses in wall-clock time, not app state, so it has to be read at click time
  const store = useStore<IAppState>();
  const products = resortMappingHistories;
  const [locked, setLocked] = React.useState(true);
  const [changedIds, setChangedIds] = React.useState<string[]>([]); // Track changed IDS for sending to api
  const [changedProducts, setChangedProducts] = React.useState<RTPMappingProduct[] | SiriuswareMappingProduct[]>([]);
  const [canEdit, setCanEdit] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [showUploadModal, setShowUploadModal] = React.useState(false);
  const [uploadSuccess, setUploadSuccess] = React.useState(false);
  const [uploadError, setUploadError] = React.useState<string | undefined>(undefined);
  const showHydrationErrors = showsHydrationErrors(userRole);
  const hydrationErrorsExpected = showHydrationErrors && resortConfig.enableResortHydrationUIFeatures;
  const [isSyncing, setIsSyncing] = React.useState(false);
  // Rows as loaded, held while editing so unsaved edits written into app state can be undone on unmount
  const [snapshot, setSnapshot] = React.useState<ResortMappingSnapshot | null>(null);
  const isRTP = products.posProvider === 'RTP';

  const clearError = useCallback((error: ApiError) => dispatch(clearApiErrorAction(error.requestId, error.actionType)), [dispatch]);
  const showError = useCallback((errorMsg: string) => dispatch(showSnackbarAction(errorMsg, true)), [dispatch]);

  const onTooltipClick = (event) => event.preventDefault();
  const onUploadButtonClick = () => setShowUploadModal(true);

  // ResortSelectMenu asks on a switch whether or not this page is mounted; this covers arriving later and
  // an ask that failed or never happened
  useEffect(() => {
    if (!resortConfig.enableResortHydrationUIFeatures) return;
    // Keyed by resort alone, so it has nothing to wait for
    if (showHydrationErrors) {
      dispatch(getProductHydrationErrorsAction(loggedInResort.resortAbbreviation));
    }
    // Mapping errors are the ask the switch also makes, so they wait for the config that says who asks
    if (!configIsForSelectedResort) return;
    // Marker read at dispatch time: as a dep it would rerun the effect that writes it
    if (resortConfig.enableProductMapping.includes(userRole ?? 'agent')
      && shouldLoadResortMappingErrors(store.getState(), loggedInResort.resortAbbreviation)) {
      dispatch(resortMappingErrorRequestedAction(loggedInResort.resortAbbreviation));
      dispatch(getResortMappingErrorAction(loggedInResort.resortAbbreviation))
    }
  }, [configIsForSelectedResort, dispatch, loggedInResort.resortAbbreviation, resortConfig.enableProductMapping, resortConfig.enableResortHydrationUIFeatures, showHydrationErrors, store, userRole])

  useEffect(() => {
    if (userRole === 'admin' || userRole === 'sysadmin') {
      setCanEdit(true);
    }    
  }, [userRole])

  // Not one of the page's loads, so no column header shows it: its failures still go to a snackbar
  useEffect(() => {
    if (recheckErrors.length > 0) {
      recheckErrors.forEach(clearError);
      showError(getResource(resources, 'PRODUCT_MAPPING_PAGE_MAPPING_STATUS_LOAD_ERROR'));
    }
  }, [clearError, recheckErrors, resources, showError])

  // Failures are cleared as soon as they are shown, so the effect below cannot read saveErrors for the outcome
  const saveFailedRef = React.useRef(false);

  useEffect(() => {
    if (saveErrors?.length >  0) {
      saveFailedRef.current = true;
      saveErrors.forEach(clearError);
      if (showUploadModal) {
        setUploadError(getResource(resources, 'PRODUCT_MAPPING_PAGE_SAVE_ERROR_MODAL'));
      } else {
        showError(getResource(resources, 'PRODUCT_MAPPING_PAGE_SAVE_ERROR'));
      }
    } 
  }, [clearError, resources, saveErrors, showError, showUploadModal]);

  // The API middleware registers a request a microtask after handleSync dispatches, so the render that sets
  // isSyncing still sees the checks settled. Wait to see one busy before calling the sync done. handleSync
  // starts only checks that will go out, so this cannot wait on one that never comes.
  const syncRequestSeen = React.useRef(false);
  const syncChecks = React.useRef<SyncCheck[]>([]);
  const [syncFailedChecks, setSyncFailedChecks] = React.useState<SyncCheck[]>([]);

  // The statuses are per resort, so a sync left in flight by a switch is never seen finishing: it
  // would latch isSyncing on, or claim success for a sync that never completed
  useEffect(() => {
    syncRequestSeen.current = false;
    setIsSyncing(false);
    setSyncFailedChecks([]);
  }, [loggedInResort.resortAbbreviation])

  useEffect(() => {
    if (!isSyncing) {
      syncRequestSeen.current = false;
      return;
    }
    const checks = syncChecks.current;
    if (checks.some(check => isBusy(status[check]))) {
      syncRequestSeen.current = true;
      return;
    }
    if (!syncRequestSeen.current) return;

    setIsSyncing(false);
    setSyncFailedChecks(checks.filter(check => hasFailed(status[check])));
    // A failed check shows next to its column's title instead
    if (checks.every(check => status[check] === 'loaded') && document.visibilityState === 'visible') {
      dispatch(showSnackbarAction(getResource(resources, 'PRODUCT_MAPPING_PAGE_SYNC_SUCCESS')));
    }
  }, [dispatch, isSyncing, resources, status])

  // A Sync shows its checks loading again rather than the values it is replacing, and dashes if it fails to replace
  // them, since the values left would pass for fresh. Other refreshes keep them.
  const shownStatus = (check: SyncCheck): LoadStatus => {
    const current = status[check];
    if (!isSyncing && !syncFailedChecks.includes(check)) return current;
    if (current === 'refreshing') return 'loading';
    return current === 'refreshFailed' ? 'failed' : current;
  };

  const changedProductsRef = React.useRef<RTPMappingProduct[] | SiriuswareMappingProduct[]>([]);
  const snapshotRef = React.useRef<ResortMappingSnapshot | null>(null);
  // Whether a save is in flight, read by the unmount cleanup
  const isSavingRef = React.useRef(false);

  // Keep ref in sync whenever state changes
  useEffect(() => {
    changedProductsRef.current = changedProducts;
  }, [changedProducts]);

  // Read through a ref so the unmount cleanup below is not rebuilt on every keystroke
  useEffect(() => {
    snapshotRef.current = snapshot;
  }, [snapshot]);

  // Edits live in app state, so leaving the page mid-edit has to put the loaded rows back. Mid-save too: a save that
  // lands writes its rows back itself, and one that fails leaves nothing to clean up.
  useEffect(() => {
    return () => {
      if (snapshotRef.current) {
        dispatch(revertResortMappingRowsAction(snapshotRef.current));
      }
      // No page will re-check what that save writes, so the next visit loads Mapping Status again
      if (isSavingRef.current) {
        dispatch(resortMappingErrorsOutOfDateAction());
      }
      // Or the resort selector stays disabled on every other page
      dispatch(setProductMappingUnsavedEditsAction(false));
    };
  }, [dispatch]);

  // changedIds empties when the PUT is dispatched, before it registers as saving. A bulk upload saves while locked.
  const hasUnsavedEdits = isSaving || (!locked && (changedIds.length > 0 || changedProducts.length > 0));

  // A resort switch clears the rows, so the selector holds off while there are edits to lose
  useEffect(() => {
    dispatch(setProductMappingUnsavedEditsAction(hasUnsavedEdits));
  }, [dispatch, hasUnsavedEdits]);

  // Reachable only in edit mode with nothing edited. Ids are only unique within a resort, so none carry over.
  const editingResortRef = React.useRef(loggedInResort.resortAbbreviation);

  useEffect(() => {
    if (editingResortRef.current === loggedInResort.resortAbbreviation) return;
    editingResortRef.current = loggedInResort.resortAbbreviation;
    setLocked(true);
    setChangedIds([]);
    setChangedProducts([]);
    setSnapshot(null);
  }, [loggedInResort.resortAbbreviation]);

  const updateChangedResortMappings = useCallback(() => {
    if (isRTP) {
      changedProductsRef.current.forEach(element => dispatch(UpdateResortMappingsRTPAction(element)));
    } else {
      changedProductsRef.current.forEach(element => dispatch(UpdateResortMappingsSWAction(element)));
    }
  }, [dispatch, isRTP])

  const wasSavingRef = React.useRef(false);

useEffect(() => {
  if (isSaving) {
    wasSavingRef.current = true;
    isSavingRef.current = true;
    saveFailedRef.current = false;
    setUploadSuccess(false);
  } else if (wasSavingRef.current && saveErrors?.length === 0) {
    wasSavingRef.current = false;
    isSavingRef.current = false;
    setLoading(false);
    if (saveFailedRef.current) {
      // Nothing saved, so stay in edit mode with the rows still marked changed: Save resends them, Cancel Edits reverts
      // them and the resort selector holds off. No re-check, which would show statuses for values the server never took.
      // A bulk upload saves while locked and never touched the rows, so it has nothing to keep.
      if (!locked) {
        const unsavedIds = (changedProductsRef.current as (RTPMappingProduct | SiriuswareMappingProduct)[]).map(product => product.id);
        setChangedIds(current => R.union(current, unsavedIds));
      }
      setChangedProducts([]);
      return;
    }
    setSnapshot(null);
    updateChangedResortMappings();
    if (!locked) setLocked(true);
    if (showUploadModal) setUploadSuccess(true);
    setChangedProducts([]);
  }
}, [isSaving, locked, saveErrors?.length, showUploadModal, updateChangedResortMappings]);


  const handleSync = () => {
    const state = store.getState();
    // Disabled while held, but a failure can trip a breaker after the render that enabled it. A dispatch would
    // then bail out, and the sync would wait on a load that never goes out; Sync's title says why.
    if (isResortMappingErrorSuppressed(state)
      || (hydrationErrorsExpected && isProductHydrationErrorsSuppressed(state))) {
      return;
    }
    // A check that is not expected has no status to watch
    syncChecks.current = [
      ...(status.mappingStatus !== 'notExpected' ? ['mappingStatus' as const] : []),
      ...(hydrationErrorsExpected ? ['hydrationErrors' as const] : []),
    ];
    setIsSyncing(syncChecks.current.length > 0);
    setSyncFailedChecks([]);
    dispatch(getResortMappingErrorAction(loggedInResort.resortAbbreviation))
    if (hydrationErrorsExpected) {
      dispatch(getProductHydrationErrorsAction(loggedInResort.resortAbbreviation));
    }
  }

  const handleDownload = () => {
    let csvString = '';
    if (isRTP) {
      csvString = convertRTPtoCSV();
    }
    else {
      csvString = convertSWtoCSV();
    }

    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    let tempLink = document.createElement('a');
    tempLink.href = url;
    tempLink.setAttribute('download', loggedInResort.resortAbbreviation + 'ProductMapping.csv');
    tempLink.click();
  }

  // A blank column reads as no errors, which a Mapping Status load that failed or has not landed cannot claim.
  // Matches the cells, so a failed Sync does not download the values it was replacing.
  const statusesUnknown = shownStatus('mappingStatus') === 'failed' || shownStatus('mappingStatus') === 'loading';
  const csvMappingStatus = (errorDescription: string) => statusesUnknown
    ? getResource(resources, 'PRODUCT_MAPPING_PAGE_CSV_UNAVAILABLE')
    : errorDescription?.replaceAll('\n', ' ').replaceAll('\r', ' ');

  const convertRTPtoCSV = () => {
    let csvString = 'EntitlementTypeMappingID,Product,Season,Age,Fulfillment Season,Product Header Code,Access Rule,Media Product Header Code,Mapping Status Error\n';
    products.productMappings.forEach((productMapping, index) => {
      const errorDescription: string = productMapping.errorDescription;
      csvString += (productMapping.id + ',' + productMapping.product + ',' + productMapping.season + ',' + productMapping.ageBin + ',' 
                    + productMapping.fulfillmentSeason + ',' + productMapping.productHeaderCode + ',' + productMapping.accessRule + ','
                    + productMapping.mediaProductHeaderCode + ',' + csvMappingStatus(errorDescription));
      if (index < products.productMappings.length - 1) {
        csvString += '\n';
      }
    });

    return csvString;
  }

  const convertSWtoCSV = () => {
    let csvString = 'EntitlementTypeMappingID,Product,Season,Age,Fulfillment Season,Department,Category,Item,Mapping Status Error\n';
    products.productMappings.forEach((productMapping, index) => {
      const errorDescription: string = productMapping.errorDescription;
      csvString += (productMapping.id + ',' + productMapping.product + ',' + productMapping.season + ',' + productMapping.ageBin + ',' 
                    + productMapping.fulfillmentSeason + ',' + productMapping.department + ',' + productMapping.category + ','
                    + productMapping.item + ',' + csvMappingStatus(errorDescription));
      if (index < products.productMappings.length - 1) {
        csvString += '\n';
      }
    });

    return csvString;
  }

  const uploadCSVtoRTP = (csvFile: File | undefined) => {
    if (csvFile) {
      setLoading(true);

      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target?.result as string;
        const lines = text.split('\n');
        //loop through csv lines
        const changedRTPProducts: RTPMappingProduct[] = [];
        lines.forEach((line, index) => {          
          //skip header line
          if(index > 0) {         
            const fields = line.split (',');
            const mappingID = fields[0];
            const productHeaderCode = fields[5];
            const accessRule = fields[6];
            const mediaHeaderCode = fields[7];
            const mappings = products.productMappings as RTPMappingProduct[];
            // Deep clone object to leave state unchanged in case upload fails
            const product = cloneDeep(mappings.find(item => String(item.id) === mappingID)) as RTPMappingProduct;
            if (product && (product.productHeaderCode !== productHeaderCode || product.accessRule !== accessRule || product.mediaProductHeaderCode !== mediaHeaderCode)){
              product.productHeaderCode = productHeaderCode;
              product.accessRule = accessRule;
              product.mediaProductHeaderCode = mediaHeaderCode;
              if (product.hydrationErrors) {
                product.hydrationErrors.errorCount = 0;
              }
              changedRTPProducts.push(product);
            }
          }
        })
        dispatch(UpdateResortMappingsAction(changedRTPProducts));
        setChangedProducts(changedRTPProducts);
        setLoading(false);
      }
      reader.readAsText(csvFile);
    }
  }

  const uploadCSVtoSW = (csvFile: File | undefined) => {
    if (csvFile) {
      setLoading(true);
      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target?.result as string;
        const lines = text.split('\n');
        //loop through csv lines
        const changedSWProducts: SiriuswareMappingProduct[] = [];
        lines.forEach((line, index) => {
          if (index > 0) {
            const fields = line.split (',');
            const mappingID = fields[0];
            const department = fields[5]?.toUpperCase();
            const category = fields[6]?.toUpperCase();
            const item = fields[7]?.toUpperCase();
            const mappings = products.productMappings as SiriuswareMappingProduct[];
            // Deep clone object to leave state unchanged in case upload fails
            const product = cloneDeep(mappings.find(x => String(x.id) === mappingID)) as SiriuswareMappingProduct;
            if (product && (product.department !== department || product.category !== category || product.item !== item)){
              product.department = department;
              product.category = category;
              product.item = item;
              if (product.hydrationErrors) {
                product.hydrationErrors.errorCount = 0;
              }
              changedSWProducts.push(product);
            }
          }
        })
        dispatch(UpdateResortMappingsAction(changedSWProducts));
        setChangedProducts(changedSWProducts);
        setLoading(false);
      }
      reader.readAsText(csvFile);
    }
  }

  const uploadDismiss = () => {
    setChangedProducts([]);
    setUploadError(undefined);
    setUploadSuccess(false);
    setShowUploadModal(false);
  }

  // Snapshot rather than refetch: instant, and the unmount path has no request to await (IRI-14483)
  const handleCancelEdits = () => {
    if (snapshot) {
      dispatch(revertResortMappingRowsAction(snapshot));
    }
    setChangedProducts([]);
    setChangedIds([]);
    setSnapshot(null);
    setLocked(true);
  }

  /**
   * Checks for changed rows and dispatches an event to update them
   * With larger product arrays there can be a long render lag, to make the render lag seem more like an async load
   * the edit and save logic is in a setTimeout with a 1ms delay
   *
   */
  const handleSave = () => {
    setLoading(true)
    if (locked) {
      setTimeout(() => {
        // Keep any existing snapshot: after a rejected save the store holds edited rows, not loaded ones
        setSnapshot(current => current ?? {
          resortAbbreviation: loggedInResort.resortAbbreviation,
          posProvider:        products.posProvider,
          rows:               cloneDeep(products.productMappings),
        });
        setLocked(false);
        setLoading(false);
      }, 1);
    } else {
      // Not off isSaving: the middleware registers the request a microtask later, too late for a fast unmount
      isSavingRef.current = true;
      setTimeout(() => {
        //setLocked(true);
        const ids = changedIds as string[];
        const changedProducts: RTPMappingProduct[] | SiriuswareMappingProduct[] = [];
        products.productMappings.forEach((product) => {
          if (ids.includes(product.id)) {
            product = Object.keys(product).reduce((acc, key) => {
              acc[key] = typeof product[key] === 'string' ? product[key].trim() : product[key];
              return acc;
            }, {});
            const productNoErrors = cloneDeep(product);
            if (productNoErrors.hydrationErrors) {
              productNoErrors.hydrationErrors.errorCount = 0;
            }
            changedProducts.push(productNoErrors);
          }
        })
        dispatch(UpdateResortMappingsAction(changedProducts));
        setChangedProducts(changedProducts);
        setChangedIds([]);
        setLoading(false)
      }, 1)
    }
  }

  // /**
  //  * Auto save every 9 minutes if there is any change
  //  * Changes to product state will force re-render which resets timer
  //  */
  // setTimeout(() => {
  //   if (changedIds.length) {
  //     handleSave();
  //   }
  // }, 1000 * 60 * 9);

  const hasRows = products.productMappings.length > 0;
  // A rows load rewrites every row's values, so editing through one would lose the edits. Mapping Status keeps unsaved
  // edits, so only Sync waits on the checks.
  const waitingOnRows = isBusy(status.rows);
  const waitingOnChecks = isBusy(status.mappingStatus) || isBusy(status.hydrationErrors);
  const syncDisabled = !hasRows || waitingOnChecks || syncHeldUntil !== undefined;
  // A zero-row result stays on screen while it refreshes, like rows do
  const showNoData = !hasRows && !isSyncing && status.rows !== 'loading' && status.rows !== 'failed';

  return (
    (resortConfig.enableProductMapping.includes(userRole ?? 'agent')) ?
      <Column rootCss={columnStyles}>
        {
          // Until rows land for the selected resort. Rows already on screen stay up while they refresh.
          (loading || status.rows === 'loading') && <IosSpinner rootCss={ioSpinner}/>
        }
        { 
          <div>
            <div css={styles.titleRow}>
              <div css={styles.titleRowLeft}>
                <span css={PAGE_TITLE}>{getResource(resources, 'PRODUCT_MAPPING_PAGE_TITLE')}</span>
                <TooltipFC
                  content={<span>{getResource(resources, 'PRODUCT_MAPPING_PAGE_INFO')}</span>}
                  children={<img alt='ⓘ' src={infoImg} css={infoStyle} onClick={onTooltipClick} />}/>
                {/*<button onClick={ () =>setIncludeOld(true)}>Include Historical Mappings</button>  NOTE: Removed for now */  }
              </div>
              <div css={styles.titleRowRight}>
                { 
                  locked && <button css={mappingButton} onClick={handleSync} disabled={syncDisabled} title={syncHeldMessage}>
                              <div css={styles.mappingButtonFlex}>
                                <img css={styles.mappingButtonImage} src={sync} alt=""/>
                                <span>{getResource(resources, 'PRODUCT_MAPPING_PAGE_SYNC')}</span>
                              </div>
                            </button>
                }
                {
                  locked && <button css={mappingButton} onClick={handleDownload} disabled={!hasRows || waitingOnRows}>
                              <div css={styles.mappingButtonFlex}>
                                <img css={styles.mappingButtonImage} src={download} alt=""/>
                                <span>{getResource(resources, 'PRODUCT_MAPPING_PAGE_DOWNLOAD')}</span>
                              </div>
                            </button>
                }
                {
                  locked && R.includes(userRole, resortConfig.bulkUploadUsers) 
                    && <button css={mappingButton} onClick={onUploadButtonClick} disabled={!hasRows || waitingOnRows}>
                              <div css={styles.mappingButtonFlex}>
                                <img css={styles.mappingButtonImage} src={upload} alt=""/>
                                <span>{getResource(resources, 'PRODUCT_MAPPING_PAGE_BULK_UPLOAD')}</span>
                              </div>
                            </button>
                }
                {
                  !locked && canEdit && <button css={mappingButton} onClick={handleCancelEdits} disabled={products.productMappings.length === 0}>
                            <div css={styles.mappingButtonFlex}>
                                <img css={styles.mappingButtonImage} src={cancel} alt=""/>
                                <span>{getResource(resources, 'PRODUCT_MAPPING_PAGE_CANCEL_EDITS')}</span>
                              </div>
                            </button>
                }
                {
                  canEdit && <button css={mappingButton} onClick={handleSave} disabled={!hasRows || (locked && waitingOnRows)}>
                              
                              {locked ? <div css={styles.mappingButtonFlex}><img css={styles.mappingButtonImage} src={pencil} alt=""/><span>{getResource(resources, 'PRODUCT_MAPPING_PAGE_EDIT_PRODUCTS')}</span></div> : 
                                        <div css={styles.mappingButtonFlex}><img css={styles.mappingButtonImage} src={save} alt=""/><span>{getResource(resources, 'PRODUCT_MAPPING_PAGE_SAVE_PRODUCTS')}</span></div>}
                            </button>
                }
              </div>
            </div>

            {
              hasRows &&
              <div css={PAGE_CONTENT}>
                  <ProductMappingTable
                      locked={locked}
                      resortMappingObject={products}
                      changedIds={changedIds}
                      setChangedIds={setChangedIds}
                      isLoadingData={(isLoadingMappingErrors || isLoading)}
                      showHydrationErrors={showHydrationErrors}
                      mappingStatus={shownStatus('mappingStatus')}
                      hydrationStatus={shownStatus('hydrationErrors')}
                  />
              </div> 
            }
            {
              showNoData &&
              <div css={css`margin-top: 25px;`}>
                <StyledBoxedMessage
                  boxStyle={BoxedNoDataMessage}
                  message={getResource(resources, 'GET_RESORT_MAPPINGS_NO_DATA')}
                />
              </div>
            }
            {
              status.rows === 'failed' &&
              <div css={css`margin-top: 25px; padding-left: 20px;`}>
                <ErrorView error={getResource(resources, 'PRODUCT_MAPPING_PAGE_LOAD_ERROR')} boxStyle={BoxedErrorInverseNoBorder} />
              </div>
            }
          </div>
        }
        {
          showUploadModal
          && <ProductMappingUploadModal
                title={getResource(resources, 'PRODUCT_MAPPING_UPLOAD_TITLE')}
                negativeButtonText={getResource(resources, 'PRODUCT_MAPPING_UPLOAD_CLOSE')}
                onNegativeButtonClick={uploadDismiss}
                onDismiss={uploadDismiss}
                uploadCSV={isRTP ? uploadCSVtoRTP : uploadCSVtoSW}
                downloadCSV={handleDownload}
                uploadSuccess={uploadSuccess}
                uploadError={uploadError}
                mappingObject={products}
          />
        }
      </Column>
      : <Navigate to={ROOT_PATH} replace/>
  )
};

export default ProductMappingPage

